import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline, mdiFood, mdiCalendarPlusOutline, mdiCalendarExportOutline, mdiCloseCircleOutline, mdiTrashCan } from '@mdi/js';
import './ClassesHelpModal.css';
import './AvailabilityHelpModal.css';

// Small inline icon for prose mentions, e.g. "the food icon ( <InlineIcon/> )"
const InlineIcon = ({ path, color }) => (
  <Icon path={path} size={0.65} color={color} style={{ verticalAlign: 'text-bottom' }} />
);

const AvailabilityHelpModal = ({ isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState([]);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!isOpen) setExpandedSections([]);
  }, [isOpen]);

  const toggleSection = (sectionId) => {
    const isExpanding = !expandedSections.includes(sectionId);
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    if (isExpanding) {
      setTimeout(() => {
        sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  };

  if (!isOpen) return null;

  const sections = [
    {
      id: 'editor-icons',
      title: '1. Editor Icons at a Glance',
      content: (
        <>
          <p>The weekly schedule, edit schedule, and travel setup screens all use the same day-row editor. Each row carries these icons:</p>
          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <Icon path={mdiFood} size={0.8} color="var(--dl-color-orange-default)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>
                <strong>Add a break.</strong> Opens a small form (start time + duration) and carves the break out of that day's availability, like a lunch hour. The break appears as its own row under the day and can be removed any time. A day can have more than one.
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <Icon path={mdiCalendarPlusOutline} size={0.8} color="var(--dl-color-turquoise-default)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>
                <strong>Add another slot group.</strong> Adds a second (or third) separate time range on the same day, for example a morning block and an evening block. Each group gets its own row with its own start and end times. Purple on the travel screens.
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <Icon path={mdiCalendarExportOutline} size={0.8} color="var(--dl-color-purple-default)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>
                <strong>Copy to other days</strong> (travel setup only). Applies this day's full setup (times, slot groups, and breaks) to any other trip dates you tick in the list that opens.
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <Icon path={mdiCloseCircleOutline} size={0.8} color="var(--dl-color-danger)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>
                <strong>Remove.</strong> Deletes that slot group or break row. The day's first time range is removed by unticking the day itself.
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  marginTop: '2px',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  gap: '2px',
                  padding: '4px',
                  border: '1px solid var(--dl-color-gray-300)',
                  borderRadius: '4px',
                }}>
                <span style={{ width: '14px', height: '2px', borderRadius: '1px', background: 'var(--dl-color-turquoise-default)' }} />
                <span style={{ width: '14px', height: '2px', borderRadius: '1px', background: 'var(--dl-color-turquoise-default)' }} />
                <span style={{ width: '14px', height: '2px', borderRadius: '1px', background: 'var(--dl-color-purple-900)' }} />
                <span style={{ width: '14px', height: '2px', borderRadius: '1px', background: 'var(--dl-color-turquoise-default)' }} />
              </span>
              <span>
                <strong>Day summary.</strong> The small rectangle at the end of each configured row previews the whole day: one line per bookable slot, with a contrasting line where a break sits. Hover it to enlarge. The "X slots" count beside each group shows how many lessons that group produces at the chosen duration.
              </span>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'published-schedules',
      title: '2. Home Schedule',
      content: (
        <>
          <p><strong>What are they?</strong> Your recurring weekly pattern published for a specific date range (e.g., "Term 1 Spring 2026: Jan 1 – Mar 31"). Each schedule has a name so you can identify it at a glance. Use the "Set weekly schedule" button to publish a new one: each weekday is its own row with its own start and end times. A day can hold several separate time ranges (add another with the calendar icon <InlineIcon path={mdiCalendarPlusOutline} color="var(--dl-color-turquoise-default)" />), and the food icon ( <InlineIcon path={mdiFood} color="var(--dl-color-orange-default)" /> ) adds a break, like lunch, that's carved out of that day's slots. The small rectangle at the end of each row previews the day's slots at a glance.</p>
          <p><strong>Can I edit a published schedule?</strong> Yes. Click the edit icon on any published schedule to update the name, date range, days, each day's times, slot groups and breaks, or the lesson duration.</p>
          <p><strong>Can I delete a published schedule?</strong> Yes. Deleting a published schedule removes it from your calendar immediately, but all existing bookings are preserved. Students' confirmed lessons remain intact and visible in your Bookings dashboard. Note: once deleted, those dates will show no availability, so students cannot make new bookings for those times.</p>
          <p><strong>Can I republish over deleted dates?</strong> Only if there are no confirmed bookings in that date range. If bookings exist, the system will block the new publish and ask you to cancel the bookings first or choose different dates.</p>
        </>
      )
    },
    {
      id: 'travel-periods',
      title: '3. Travel Periods',
      content: (
        <>
          <p><strong>What are they?</strong> Separate availability when you're teaching at a different location (workshops, festivals, guest teaching). Switch to Travel mode using the <strong>Travel Schedule</strong> tab in the sidebar. Your home availability and travel availability are managed separately. Use the sidebar tabs to switch between them.</p>
          <p><strong>Home schedule during travel:</strong> When a travel period is active, your home recurring schedule is <strong>automatically suppressed</strong> for those dates. Students cannot book your home location during a travel period. Only your travel availability is visible to them.</p>
          <p><strong>Tip: consider using teaching days, not travel days.</strong> If you fly out on a Friday and return Tuesday, but only teach Saturday–Monday, you may want to set the travel period to Saturday–Monday only. This keeps Friday and Tuesday visible as normal home availability. That said, you can include travel days if you prefer; it's entirely up to you. If a travel day has no slots set, students simply won't see anything to book for it.</p>
          <p><strong>Overlapping travel periods:</strong> If you create a travel period whose dates overlap an existing one, a warning will appear. This can happen legitimately, for example if you are travelling between two destinations and the changeover day falls within both periods. In that case you can proceed, but make sure that changeover day has no slots set (or only the correct destination's slots) so students are not shown conflicting availability. If the overlap was a mistake, click <strong>Go Back to fix the dates</strong>.</p>
          <p><strong>Creating a travel period when home bookings exist:</strong> If you already have confirmed home-schedule bookings on the dates you are adding as a travel period, the system will detect this and show you a list of affected bookings before saving. You can cancel those bookings and notify the students in one step, then the travel period is created. Unpaid bookings are cancelled together. Each paid booking needs a choice of Refund or Credit before you can confirm (Credit is not offered for guest bookings). If you would rather keep those bookings, click Go Back and choose different dates for your travel period.</p>
          <p><strong>Date changes: slots outside the new range are removed.</strong> If you change a travel period's dates (shorten, extend the start, or move it entirely), any slots you set on dates that no longer fall within the new range are automatically deleted. You will see a warning toast telling you how many slots were removed so you know to re-add them for the new dates. If active home-schedule bookings exist within the new date range, the same conflict modal appears so you can cancel those bookings in one step before the update is saved.</p>
          <p><strong>Deletion with active bookings:</strong> If active bookings exist, clicking the trash icon ( <InlineIcon path={mdiTrashCan} color="var(--dl-color-danger)" /> ) opens a confirmation modal listing all affected bookings. You can edit the cancellation message sent to students, then confirm. Unpaid bookings are cancelled together. Each paid booking needs a choice of Refund or Credit before you can confirm (Credit is not offered for guest bookings). The bookings are cancelled and students notified in one step, with no need to cancel them separately beforehand.</p>
          <p><strong>Overriding your rate for a trip:</strong> When you create a travel period, the rate and currency come pre-filled with your default hourly rate and your Base Currency. Leave them as they are and the trip simply follows your default rate, so if you change your default rate later, new bookings for the trip use the new rate too. Change either one only when this trip should differ. If you want to charge in a different currency, set the rate and the currency together: a travel currency always needs a rate, so you cannot have one without the other. Any booking on a date inside the period is then priced at that rate and currency instead of your home hourly rate. Keeping your Base Currency bills in it (foreign venue fees are converted to your Base Currency). If you have not set a default hourly rate on your instructor profile yet, the rate field starts empty with a link to set one. Note: if a student holds account credit in your Base Currency, it cannot be applied to a booking priced in a different currency (they can pay by card instead).</p>
          <p><strong>Charging a trip in another currency:</strong> Students then pay in that currency, which usually avoids a conversion fee from their bank. You are still paid in your Base Currency, converted at the exchange rate on the day each student pays, so the amount you receive moves with the exchange rate. You pay no conversion fee. If you keep your Base Currency, overseas students' banks convert the payment and usually add a foreign transaction fee. When you save a currency other than your Base Currency, a short confirm screen appears. Your Base Currency is shown in Settings, under Account Details, and matches your payout bank account country.</p>
          <p><strong>Changing a trip's currency later:</strong> Once a travel period has an active booking, its currency is locked. To change it, cancel those bookings first. The edit window lists them and has a <strong>Cancel these bookings</strong> button. Unpaid bookings are cancelled together. Each paid booking needs a choice of Refund or Credit before you can confirm (Credit is not offered for guest bookings). Students are notified. You can change the rate at any time. A new rate applies to new bookings only. Existing bookings keep their price and currency.</p>
        </>
      )
    },
    {
      id: 'travel-slots',
      title: '4. Travel Schedule: Adding & Editing Slots',
      content: (
        <>
          <p><strong>Initial setup (right after creating a travel period):</strong> As soon as you create a new travel period, a setup screen appears automatically with one row per day of the trip. Tick the days you'll teach and set each day's hours right on its row. Add extra time ranges ( <InlineIcon path={mdiCalendarPlusOutline} color="var(--dl-color-purple-default)" /> ) or a break ( <InlineIcon path={mdiFood} color="var(--dl-color-orange-default)" /> ) with the icons beside the times. The copy icon ( <InlineIcon path={mdiCalendarExportOutline} color="var(--dl-color-purple-default)" /> ) applies one day's setup to any other days you choose. Save once for the whole trip.</p>
          <p><strong>Adding slots to an empty day later:</strong> In Travel mode, select your travel period from the sidebar, then look at the week grid. Days with no slots show a placeholder card spanning 8am–5pm. Click that card to set up that day. The same row editor opens for just that date.</p>
          <p><strong>Editing slots on a day that already has them:</strong> Click any existing slot on the week grid to open the day editor. You can add new slots or delete individual ones. Booked slots cannot be deleted; cancel the booking first.</p>
        </>
      )
    },
    {
      id: 'daily-editing',
      title: '5. Home Schedule: Editing Times for a Specific Day',
      content: (
        <>
          <p><strong>How day editing works:</strong> Your weekly schedule is the foundation. When you edit a specific day, you're making day-specific changes that sit <em>on top of</em> the recurring schedule. The weekly schedule itself is never changed. Adding and removing are two separate gestures: click an empty part of the day to add, click an existing slot to remove.</p>
          <p><strong>Adding times to a day:</strong> Hover over an empty time on the day in the week grid and click the "+" (it only appears on hours with no existing slot). A small form opens prefilled with that start time: choose the slot length, then adjust the start and end times. Use the -15 / +15 buttons to land on quarter-hour starts like 9:15 or 9:30, and extend the end time to add several consecutive slots. A live preview shows exactly what will be added. This adds one-off times for that day only; your recurring schedule and other days are unaffected.</p>
          <p><strong>Overlap protection:</strong> If the times you're adding would overlap availability that already exists on that day, a warning appears and the Add button stays disabled until you adjust the start time or slot length. This stops overlapping availability from being created.</p>
          <p><strong>Removing times:</strong> Click any existing slot on that day to open the day editor. It shows the day's current slots on the left and what you'll be left with on the right. Click the remove control on a slot to mark it (it turns red and is struck through, and you can click again to undo), then Save.</p>
          <ul>
            <li><strong>Slots from your weekly schedule:</strong> Blocked for this day only. Your weekly schedule is unchanged and the time appears as normal on all other days.</li>
            <li><strong>Slots you added just for today:</strong> Deleted entirely.</li>
            <li><strong>Booked slots:</strong> Cannot be removed. Cancel the booking first in your Bookings dashboard.</li>
          </ul>
          <p><strong>Bringing back a blocked time:</strong> To restore a weekly time you blocked on a day, add it back as a one-off using the add steps above. Your weekly schedule on other days is never affected either way.</p>
          <p><strong>"Block dates" button:</strong> A separate action that blocks entire days. Choose a Type (Public Holiday, Personal Day, or Annual Leave) so each kind of unavailability renders in its own distinct colour on your calendar. Unlike adding or removing individual times, it affects full days rather than single time slots.</p>
        </>
      )
    },
    {
      id: 'calendar-legend',
      title: '6. Reading the Calendar Legend',
      content: (
        <>
          <p>The legend above the week grid shows what each slot type looks like:</p>
          <ul>
            <li><strong>Standard Schedule:</strong> Times from your published weekly schedule. These repeat automatically on matching days within your published date range.</li>
            <li><strong>Custom Hours:</strong> One-off times you've added for a specific day, or a weekly time you've blocked on that day.</li>
            <li><strong>Travel Teaching:</strong> Availability you've set up for a travel period. These only appear when you have an active travel period selected in the sidebar.</li>
          </ul>
          <p><em>Tip: When editing a specific day, each time slot also shows a small label indicating where it came from and what happens if you remove it. Hover the label for details.</em></p>
        </>
      )
    },
    {
      id: 'booking-protection',
      title: '7. Booking Protection Rules',
      content: (
        <>
          <p><strong>Core principle:</strong> Confirmed bookings are protected. You cannot accidentally remove times that students have booked.</p>
          <p><strong>What you cannot remove:</strong></p>
          <ul>
            <li>Individual time slots with confirmed bookings (shown as booked in the day edit view). Cancel the booking in your Bookings dashboard first.</li>
          </ul>
          <p><strong>Deleting a published schedule:</strong> The schedule is removed and its slots disappear from the calendar, but all existing confirmed bookings remain safe. Students keep their lessons.</p>
          <p><strong>Republishing over deleted dates:</strong> If confirmed bookings exist in the date range, the system will block the new publish. You must cancel those bookings or choose a different date range first.</p>
          <p><strong>To free up a booked time:</strong> Go to your Bookings dashboard and cancel the booking there. The time slot will then become editable again.</p>
        </>
      )
    },
    {
      id: 'quick-reference',
      title: '8. What You Can/Cannot Do',
      content: (
        <>
          <table className="help-reference-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Allowed?</th>
                <th>How To</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Publish a new weekly schedule</td>
                <td>✅ Yes*</td>
                <td>Use "Set weekly schedule" button (*blocked if confirmed bookings overlap)</td>
              </tr>
              <tr>
                <td>Add times to a day</td>
                <td>✅ Yes</td>
                <td>Hover an empty time on the day and click the "+", pick the slot length, adjust start/end, then Add times</td>
              </tr>
              <tr>
                <td>Remove times from a day</td>
                <td>✅ Yes*</td>
                <td>Click a slot on that day, mark it for removal, then Save (*booked slots cannot be removed)</td>
              </tr>
              <tr>
                <td>Add times that overlap existing availability</td>
                <td>❌ No</td>
                <td>Blocked. A warning shows and Add stays disabled until you adjust the start time or slot length</td>
              </tr>
              <tr>
                <td>Block entire days</td>
                <td>✅ Yes</td>
                <td>Click "Block dates" button on any day</td>
              </tr>
              <tr>
                <td>Add slots to a new travel period</td>
                <td>✅ Yes</td>
                <td>Setup screen opens automatically after creation. Configure days then save.</td>
              </tr>
              <tr>
                <td>Add slots to an empty travel day</td>
                <td>✅ Yes</td>
                <td>In Travel mode, click the placeholder card on that day in the week grid</td>
              </tr>
              <tr>
                <td>Edit/delete slots on a travel day</td>
                <td>✅ Yes*</td>
                <td>Click any existing slot on the week grid (*booked slots cannot be deleted)</td>
              </tr>
              <tr>
                <td>Edit travel period dates</td>
                <td>✅ Yes</td>
                <td>Click travel period card, edit dates. If home-schedule bookings exist on the new dates, a conflict modal appears so you can cancel them in one step before saving. Unpaid bookings are cancelled together; each paid booking needs Refund or Credit chosen first (no Credit for guests). Students are notified.</td>
              </tr>
              <tr>
                <td>Delete travel period</td>
                <td>✅ Yes</td>
                <td>Click trash icon. If active bookings exist, a confirmation modal lets you edit the cancellation message and cancel all bookings in one step. Unpaid bookings are cancelled together; each paid booking needs Refund or Credit chosen first (no Credit for guests). Students are notified.</td>
              </tr>
              <tr>
                <td>Remove a booked time slot</td>
                <td>❌ No</td>
                <td>Cancel the booking first in Bookings dashboard</td>
              </tr>
              <tr>
                <td>Change a day's break times</td>
                <td>✅ Yes</td>
                <td>Click edit on the schedule card, remove the day's break row, then add a new break with the food icon ( <InlineIcon path={mdiFood} color="var(--dl-color-orange-default)" /> )</td>
              </tr>
              <tr>
                <td>Rename a schedule</td>
                <td>✅ Yes</td>
                <td>Click edit icon on the schedule card</td>
              </tr>
              <tr>
                <td>Run two schedules at the same time</td>
                <td>❌ No</td>
                <td>Date ranges cannot overlap. Publish back-to-back, not overlapping.</td>
              </tr>
              <tr>
                <td>Create overlapping travel periods</td>
                <td>⚠️ With warning</td>
                <td>A warning appears asking you to confirm. Legitimate for back-to-back destinations with a shared changeover day. Avoid if it was a date entry mistake; use "Go Back" and correct the dates instead. Tip: setting periods to teaching days rather than travel days reduces the chance of accidental overlaps.</td>
              </tr>
            </tbody>
          </table>
        </>
      )
    }
  ];

  return (
    <div className="availability-help-overlay" onClick={onClose}>
      <div className="availability-help-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="availability-help-header">
          <div className="availability-help-header-content">
            <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
            <h2>Availability Management Help</h2>
          </div>
          <button className="availability-help-close" onClick={onClose}>×</button>
        </div>

        {/* Accordion Sections */}
        <div className="availability-help-body">
          {sections.map(section => (
            <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
              <button
                className="help-accordion-header"
                onClick={() => toggleSection(section.id)}
              >
                <Icon
                  path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                  size={0.8}
                  color="var(--dl-color-purple-default)"
                />
                <span>{section.title}</span>
              </button>
              {expandedSections.includes(section.id) && (
                <div className="help-accordion-content">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="availability-help-footer">
          <button className="classes-help-btn-secondary" onClick={onClose}>Got it!</button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityHelpModal;
